/**
 * ChooseServicePage
 * ---------------------
 * Reached from the free preview: the client has already captured and seen what
 * was detected, and now picks which analysis they want. Choosing the service
 * fixes the agent that will run.
 *
 * Still free — the next step is choosing a doctor, and payment comes after
 * that.
 */
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Typography,
  Stack,
  Card,
  CardContent,
  Button,
  Chip,
  Alert,
  Skeleton,
  ButtonBase,
} from '@mui/material';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import VerifiedIcon from '@mui/icons-material/Verified';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import Container from '@/components/PageSheet';
import FlowSummary from '@/components/FlowSummary';
import { servicesApi, consultationsApi } from '@/services/api';
import { localized } from '@/utils/localizedContent';
import { formatToman } from '@/utils/currency';

export default function ChooseServicePage() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const isRtl = i18n.dir() === 'rtl';
  const navigate = useNavigate();
  const { consultationId } = useParams();
  const qc = useQueryClient();
  const [error, setError] = useState('');
  const Chevron = isRtl ? ChevronLeftIcon : ChevronRightIcon;

  const consultationQuery = useQuery({
    queryKey: ['consultation', consultationId],
    queryFn: () => consultationsApi.getConsultation(consultationId),
  });

  const servicesQuery = useQuery({
    queryKey: ['services'],
    queryFn: () => servicesApi.listServices(),
  });

  const termsQuery = useQuery({
    queryKey: ['consultation-terms'],
    queryFn: () => consultationsApi.getConsultationTerms(),
    staleTime: 5 * 60 * 1000,
  });

  const all = servicesQuery.data?.data?.services ?? servicesQuery.data?.services ?? [];
  const consultationServices = all.filter((s) => s.requiresPhysician);
  const terms = termsQuery.data || {};

  const startMut = useMutation({
    mutationFn: (serviceId) =>
      consultationsApi.setConsultationService(consultationId, serviceId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['my-consultations'] });
      qc.invalidateQueries({ queryKey: ['consultation', consultationId] });
      // The doctor is already chosen; this is the last step before payment.
      navigate(`/consultations/${encodeURIComponent(consultationId)}`);
    },
    onError: (err) =>
      setError(err?.response?.data?.error?.message || t('common.error')),
  });

  const loading = servicesQuery.isLoading;

  return (
    <Container maxWidth="sm" sx={{ py: { xs: 2, sm: 4 } }}>
      <Typography variant="h6" fontWeight={800}>
        {t('chooseService.title')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('chooseService.subtitle')}
      </Typography>

      <FlowSummary consultation={consultationQuery.data} lang={lang} t={t} />

      {/* The promise, before any price is shown. */}
      <Card
        elevation={0}
        sx={{ mb: 2, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}
      >
        <CardContent>
          <Stack spacing={1.25}>
            <Promise
              icon={<PhotoCameraIcon fontSize="small" />}
              text={t('startConsultation.promiseFree')}
            />
            <Promise
              icon={<VerifiedIcon fontSize="small" />}
              text={t('startConsultation.promiseSla', { hours: terms.slaHours ?? 48 })}
            />
            <Promise
              icon={<ChatBubbleOutlineIcon fontSize="small" />}
              text={t('startConsultation.promiseChat', { days: terms.chatDays ?? 7 })}
            />
          </Stack>
        </CardContent>
      </Card>


      {error && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 3 }}>
          {error}
        </Alert>
      )}

      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        {t('chooseService.pickService')}
      </Typography>

      {loading ? (
        <Stack spacing={1.5}>
          {[0, 1].map((i) => (
            <Skeleton key={i} variant="rounded" height={92} />
          ))}
        </Stack>
      ) : consultationServices.length === 0 ? (
        <Card
          elevation={0}
          sx={{
            borderRadius: 3,
            border: '1px dashed',
            borderColor: 'divider',
            textAlign: 'center',
            py: 5,
          }}
        >
          <Typography fontWeight={800}>{t('startConsultation.emptyTitle')}</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {t('startConsultation.emptyBody')}
          </Typography>
          <Button sx={{ mt: 1.5 }} onClick={() => navigate('/services')}>
            {t('startConsultation.browseAll')}
          </Button>
        </Card>
      ) : (
        <Stack spacing={1.5}>
          {consultationServices.map((s) => (
            <ButtonBase
              key={s.id}
              disabled={startMut.isPending}
              onClick={() => startMut.mutate(s.id)}
              sx={{ display: 'block', width: '100%', textAlign: 'start', borderRadius: 3 }}
            >
              <Card
                elevation={0}
                sx={{
                  width: '100%',
                  borderRadius: 3,
                  border: '1px solid',
                  borderColor: 'divider',
                  transition: 'border-color .2s',
                  '&:hover': { borderColor: 'primary.main' },
                }}
              >
                <CardContent>
                  <Stack direction="row" alignItems="center" spacing={1.5}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography fontWeight={800} noWrap>
                        {localized(s.name, lang)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                        {localized(s.description, lang)}
                      </Typography>
                      <Stack direction="row" spacing={1} sx={{ mt: 1 }} alignItems="center">
                        <Chip size="small" color="secondary" label={t('startConsultation.freeChip')} />
                        {s.priceToman != null && (
                          <Typography variant="caption" color="text.secondary">
                            {t('startConsultation.thenPay', {
                              price: formatToman(s.priceToman, lang),
                            })}
                          </Typography>
                        )}
                      </Stack>
                    </Box>
                    <Chevron sx={{ color: 'text.disabled' }} />
                  </Stack>
                </CardContent>
              </Card>
            </ButtonBase>
          ))}
        </Stack>
      )}
    </Container>
  );
}

function Promise({ icon, text }) {
  return (
    <Stack direction="row" spacing={1.25} alignItems="flex-start">
      <Box sx={{ color: 'secondary.main', mt: '2px', display: 'flex' }}>{icon}</Box>
      <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
        {text}
      </Typography>
    </Stack>
  );
}
